import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Clock, Check, ArrowRight, Crown } from 'lucide-react';
import api from '../utils/api';

const ActivationBanner = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await api.get('/activation/status');
        setStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch activation status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  // Timer countdown effect
  useEffect(() => {
    if (status?.is_activated && status?.expires_at) {
      const updateTimer = () => {
        const now = new Date();
        const expiry = new Date(status.expires_at);
        const diff = expiry - now;

        if (diff <= 0) {
          setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
          return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        setTimeRemaining({ days, hours, minutes, seconds, expired: false });
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [status]);

  if (loading) {
    return (
      <div className="glass-card p-6 animate-pulse">
        <div className="h-20 bg-white/10 rounded-xl"></div>
      </div>
    );
  }

  if (!status) return null;

  // User is activated - Show timer countdown
  if (status.is_activated) {
    return (
      <div 
        className="glass-card p-6 border-2 border-green-500/30 bg-gradient-to-r from-green-500/10 to-emerald-500/10"
        data-testid="activation-banner-active"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-green-500/20 rounded-2xl flex items-center justify-center">
              <Check className="w-7 h-7 text-green-500" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-5 h-5 text-primary" />
                <h3 className="text-xl font-bold text-green-500">Digital Access Pass Active</h3>
              </div>
              <p className="text-muted-foreground">
                {status.activation?.pack_name} • You can now earn commissions!
              </p>
            </div>
          </div>
          
          {/* Countdown Timer - DD:HH:MM:SS format */}
          {timeRemaining && !timeRemaining.expired && (
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Time to Renew Digital Access Pass</p>
              <div className="bg-black/30 px-6 py-3 rounded-xl border border-green-500/30">
                <p className="text-3xl md:text-4xl font-bold text-green-500 font-mono tracking-wider">
                  {String(timeRemaining.days).padStart(2, '0')}:{String(timeRemaining.hours).padStart(2, '0')}:{String(timeRemaining.minutes).padStart(2, '0')}:{String(timeRemaining.seconds).padStart(2, '0')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Days : Hours : Minutes : Seconds</p>
              </div>
            </div>
          )}
          
          {timeRemaining?.expired && (
            <Link to="/activation" className="btn-primary flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Renew Digital Access Pass
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Pending approval status
  if (status.status === 'pending') {
    return (
      <div 
        className="glass-card p-6 border-2 border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 to-orange-500/10"
        data-testid="activation-banner-pending"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-yellow-500/20 rounded-2xl flex items-center justify-center">
              <Clock className="w-7 h-7 text-yellow-500 animate-pulse" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-yellow-500">Digital Access Pass Pending</h3>
              <p className="text-muted-foreground">
                Your request is awaiting admin approval. Please complete payment if not done.
              </p>
            </div>
          </div>
          <Link to="/activation" className="px-6 py-3 bg-yellow-500/20 text-yellow-500 rounded-full font-semibold">
            View Status
          </Link>
        </div>
      </div>
    );
  }

  // User needs activation - Show prominent CTA
  return (
    <div 
      className="relative overflow-hidden glass-card p-6 border-2 border-primary/50"
      data-testid="activation-banner-required"
    >
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 animate-gradient-x"></div>
      
      {/* Blinking indicator */}
      <div className="absolute top-4 right-4">
        <span className="relative flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-primary"></span>
        </span>
      </div>

      <div className="relative flex items-center justify-between flex-wrap gap-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center animate-pulse">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-1">
              Get Your <span className="text-primary">Digital Access Pass</span>
            </h3>
            <p className="text-muted-foreground">
              Unlock Affiliate Network features, earn commissions, and build your network!
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-semibold">
                Monthly
              </span>
              <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-sm font-semibold">
                Quarterly
              </span>
              <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm font-semibold">
                Yearly
              </span>
            </div>
          </div>
        </div>
        
        <Link
          to="/activation"
          className="btn-primary flex items-center gap-2 text-lg px-8 py-4 animate-bounce"
          data-testid="activate-now-btn"
        >
          <Zap className="w-5 h-5" />
          Get Access Pass
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>

      <style jsx>{`
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default ActivationBanner;
